import { cn } from '@/shared/lib';
import { Dialog, DialogContent, DialogTitle } from '@/shared/ui/dialog';
import { Maximize2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';

interface ProfilePhotoProps {
  src?: string;
  alt: string;
  /** shown when there is no photo (initial letter) */
  fallback: ReactNode;
  className?: string;
  children?: ReactNode;
}

/**
 * Member photo at the upload ratio (3:4, same as the signup cropper) so the
 * whole cropped photo is visible. Clicking it opens the photo full screen,
 * centered and uncropped (object-contain). `children` are overlays (badges).
 */
export function ProfilePhoto({ src, alt, fallback, className, children }: ProfilePhotoProps) {
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const hasPhoto = !!src && !failed;

  return (
    <div className={cn('relative aspect-[3/4] overflow-hidden bg-primary-surface', className)}>
      {hasPhoto ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="group block h-full w-full cursor-zoom-in"
          aria-label={alt}
          data-testid="profile-photo-open"
        >
          <img
            src={src}
            alt={alt}
            className="h-full w-full object-cover"
            onError={() => setFailed(true)}
          />
          <span className="absolute right-3 bottom-3 rounded-full bg-black/50 p-2 text-white opacity-0 transition-opacity group-hover:opacity-100">
            <Maximize2 className="h-4 w-4" />
          </span>
        </button>
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary to-primary-dark text-7xl text-primary-foreground">
          {fallback}
        </div>
      )}
      {children}

      {hasPhoto && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="max-w-[95vw] border-0 bg-black/95 p-2 sm:max-w-[95vw] [&>button]:text-white [&>button]:opacity-90"
            data-testid="profile-photo-lightbox"
          >
            <DialogTitle className="sr-only">{alt}</DialogTitle>
            <img src={src} alt={alt} className="mx-auto max-h-[90vh] w-auto object-contain" />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
